import { useSyncExternalStore, useCallback } from 'react';
import { artistsData as initialArtists } from '../mocks/artists';

export interface PlayerData {
  trackName: string;
  trackLabel: string;
  duration: string;
  playerSnsUrl?: string;
}

export interface CreditData {
  name: string;
  genre: string;
  location: string;
  description: string;
  imageUrl: string;
  artistUserId: string;
  snsUrl: string;
}

export interface SocialData {
  platform: string;
  icon: string;
  username: string;
  url: string;
}

export interface MockArtist {
  id: number;
  name: string;
  location: string;
  prefecture: string;
  genre: string;
  image: string;
  platforms: string[];
  color: string;
  playerData?: PlayerData | null;
  creditsData?: CreditData[] | null;
  socialsData?: SocialData[] | null;
}

const LS_KEY = 'admin_artists_data';

function loadFromStorage(): MockArtist[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return [...initialArtists];
}

function saveToStorage(data: MockArtist[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

// Global store outside of React
let artists: MockArtist[] = loadFromStorage();
let cachedSnapshot: MockArtist[] = artists;
const listeners = new Set<() => void>();

function emitChange() {
  // Update cached snapshot when state changes
  cachedSnapshot = artists;
  saveToStorage(artists);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): MockArtist[] {
  // Return the same reference until state actually changes
  return cachedSnapshot;
}

function addArtistGlobal(artist: MockArtist) {
  // Guard against duplicate IDs
  if (artists.some((a) => a.id === artist.id)) {
    console.error(`Artist with id ${artist.id} already exists.`);
    return;
  }
  artists = [...artists, artist];
  emitChange();
}

function updateArtistGlobal(id: number, data: Partial<MockArtist>) {
  const exists = artists.some((a) => a.id === id);
  if (!exists) {
    console.error(`Cannot update: No artist found with id ${id}.`);
    return;
  }
  artists = artists.map((a) => (a.id === id ? { ...a, ...data } : a));
  emitChange();
}

function deleteArtistGlobal(id: number) {
  const exists = artists.some((a) => a.id === id);
  if (!exists) {
    console.error(`Cannot delete: No artist found with id ${id}.`);
    return;
  }
  artists = artists.filter((a) => a.id !== id);
  emitChange();
}

// Get artist by id (non-hook, can be called anywhere)
export function getArtistById(id: number): MockArtist | undefined {
  return artists.find((a) => a.id === id);
}

// Get all artists (non-hook)
export function getAllArtists(): MockArtist[] {
  // Return a copy to keep the store immutable from the outside
  return [...artists];
}

export function useArtistStore() {
  const currentArtists = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const addArtist = useCallback((artist: MockArtist) => {
    addArtistGlobal(artist);
  }, []);

  const updateArtist = useCallback((id: number, data: Partial<MockArtist>) => {
    updateArtistGlobal(id, data);
  }, []);

  const deleteArtist = useCallback((id: number) => {
    deleteArtistGlobal(id);
  }, []);

  return { artists: currentArtists, addArtist, updateArtist, deleteArtist };
}
